/**
 * Chip-toggle editor for objective tags. Used in two places: per-scene
 * `node.objectiveTags` and per-choice `choice.objectiveTags`. Tags are
 * stored as arrays of objective ids (not codes — codes can change, ids
 * are stable).
 *
 * Visual:
 *   - Each declared objective renders as a small pill labeled with its
 *     `code`. The pill is filled with the objective's palette color when
 *     tagged, and outlined when not.
 *   - Click to toggle. The full list is always shown; there's no separate
 *     "selected" vs "available" partition because the partition is
 *     immediately obvious from the fill.
 *   - Hover shows the objective's `description` as a tooltip.
 *
 * Empty state: when no objectives are declared, the editor shows the
 * "Objectives modal arrives in Phase 1b.3" CTA — same honest empty-state
 * pattern as ConditionBuilder and EffectsEditor.
 *
 * Mismatch resilience: if a tag points at an objective id that's no
 * longer declared, that tag is silently ignored by the renderer (the
 * validator will surface it as `UNDECLARED_OBJECTIVE`). When the author
 * toggles any *remaining* declared tag, the orphan tag is preserved in
 * the underlying array — we don't garbage-collect it from the editor
 * because that would mask the validation issue.
 */
import type { ObjectiveDecl } from '@branchworks/shared';
import { objectiveColor } from '../ObjectivePalette';
import { useScenarioStore } from '../../store/scenarioStore';

interface ObjectiveTagsEditorProps {
  /** Currently-selected objective ids on the node or choice. */
  selectedIds: string[] | undefined;
  /** All declared objectives in the scenario. */
  objectives: Record<string, ObjectiveDecl>;
  onChange: (next: string[]) => void;
}

export function ObjectiveTagsEditor({
  selectedIds,
  objectives,
  onChange,
}: ObjectiveTagsEditorProps) {
  const selected = selectedIds || [];
  const declaredIds = Object.keys(objectives);
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);

  if (declaredIds.length === 0) {
    return (
      <div className="text-xs italic text-ink-3">
        No objectives declared.{' '}
        <button
          onClick={() => setOpenModal('objectives')}
          className="not-italic underline text-ink-2 hover:text-ink"
        >
          Declare one
        </button>
        {' '}to tag this.
      </div>
    );
  }

  const toggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange(next);
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {declaredIds.map((id) => {
        const obj = objectives[id];
        const isSelected = selected.includes(id);
        const color = objectiveColor(objectives, id);
        return (
          <button
            key={id}
            onClick={() => toggle(id)}
            title={obj.description ? `${obj.code} — ${obj.description}` : obj.code}
            className="text-[11px] px-2 py-1 rounded-full border transition-colors"
            style={{
              background: isSelected ? color : 'transparent',
              borderColor: color,
              color: isSelected ? '#FFFEFB' : color,
              fontWeight: isSelected ? 600 : 500,
            }}
          >
            {obj.code || id}
          </button>
        );
      })}
    </div>
  );
}
